import React, { useState } from "react";
import { getStatusStyle, SocialProfileState } from "@/components/SocialNetworkProfiles/components";
import { socialNetworkConnectionNames, socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { Button, DropdownMenu, Skeleton, Tooltip } from "@/components/ui";
import {
  ArrowLeftRight,
  Download,
  HelpCircle,
  LifeBuoy,
  Link2Off,
  MoreVertical,
  RefreshCw,
  Settings,
  Shield,
} from "lucide-react";
import { SocialSvg } from "@/components/SocialSvg";
import { useSocialCoachSession } from "@/utils";

interface Props {
  profileState?: SocialProfileState;
  networkId: SocialNetworksId;
  isLoading: boolean;
  showMenu: boolean;
  menuDisabled: boolean;
  onOpenChange: (open: boolean) => void;
  disconnect: () => void;
  sync?: () => void;
  switchAccounts?: () => void;
  importAllZillowReviews?: () => void;
  configure: () => void;
  handleNeedHelp: () => void;
  onEditReviewPrefs?: () => void;
  isChild?: boolean;
}

export function SocialProfileCardHeader({
  profileState,
  networkId,
  isLoading,
  showMenu,
  menuDisabled,
  disconnect,
  sync,
  switchAccounts,
  importAllZillowReviews,
  configure,
  onOpenChange,
  handleNeedHelp,
  onEditReviewPrefs,
  isChild,
}: Props) {
  const { isPlayer } = useSocialCoachSession();
  const statusStyle = getStatusStyle(profileState);

  const [isConfirmingDisconnect, setIsConfirmingDisconnect] = useState(false);

  const handleMenuOpenChange = (open: boolean) => {
    if (!open) {
      setTimeout(() => setIsConfirmingDisconnect(false), 200);
    }
    onOpenChange(open);
  };

  const handleDisconnectClick = (e: React.MouseEvent) => {
    if (!isConfirmingDisconnect) {
      e.preventDefault();
      e.stopPropagation();
      setIsConfirmingDisconnect(true);
    } else {
      disconnect();
      setIsConfirmingDisconnect(false);
    }
  };

  return (
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <SocialSvg
          networkId={networkId}
          iconColorClass={profileState === "NOT_LINKED" ? "text-slate-300" : undefined}
        />
        <div className="flex flex-col">
          <span className="font-bold text-lg text-foreground">{socialNetworkNames[networkId]}</span>
          {isLoading || !statusStyle ? (
            <Skeleton className="h-[14px] w-20 mt-1 rounded-sm" />
          ) : (
            <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wide uppercase mt-1">
              {isChild ? (
                <Tooltip.Provider delay={100}>
                  <Tooltip>
                    <Tooltip.Trigger>
                      <span
                        className={`flex items-center justify-center cursor-help transition-colors ${statusStyle.color} opacity-85 hover:opacity-100`}
                      >
                        <Shield className="size-3.5 shrink-0" fill="currentColor" />
                      </span>
                    </Tooltip.Trigger>
                    <Tooltip.Content
                      side="top"
                      className="max-w-xs text-center p-2 rounded-md font-semibold font-sans normal-case tracking-normal bg-foreground text-background"
                    >
                      Managed Connection: This {socialNetworkConnectionNames[networkId]} is connected and managed
                      centrally by your administrator.
                    </Tooltip.Content>
                  </Tooltip>
                </Tooltip.Provider>
              ) : (
                <span className={`w-2 h-2 rounded-full ${statusStyle.bg}`} />
              )}
              <span className={statusStyle.color}>{statusStyle.text}</span>
            </div>
          )}
        </div>
      </div>

      {showMenu ? (
        <DropdownMenu onOpenChange={handleMenuOpenChange} disabled={menuDisabled}>
          <DropdownMenu.Trigger
            render={props => (
              <Button
                {...props}
                variant="outline"
                size="icon"
                className="rounded-full text-slate-500 hover:text-slate-600 hover:bg-slate-50 transition-colors p-1.5 outline-none border-none"
              >
                <span className="sr-only">Open menu</span>
                <MoreVertical size={25} />
              </Button>
            )}
          />
          <DropdownMenu.Content align="end" className="w-48">
            <DropdownMenu.Group>
              {networkId === "TT" && (
                <DropdownMenu.Item onClick={configure}>
                  <Settings className="mr-2 h-4 w-4 text-slate-500" />
                  <span>Settings</span>
                </DropdownMenu.Item>
              )}

              {sync && (
                <DropdownMenu.Item onClick={sync}>
                  <RefreshCw className="mr-2 h-4 w-4 text-slate-500" />
                  <span>Sync Profile</span>
                </DropdownMenu.Item>
              )}

              {!isPlayer && switchAccounts && (
                <DropdownMenu.Item onClick={switchAccounts}>
                  <ArrowLeftRight className="mr-2 h-4 w-4 text-slate-500" />
                  <span>Switch Accounts</span>
                </DropdownMenu.Item>
              )}

              {importAllZillowReviews && (
                <DropdownMenu.Item
                  onClick={importAllZillowReviews}
                  className="hidden"
                  data-sc-dev-only="zillow-import-reviews"
                  aria-hidden
                  tabIndex={-1}
                >
                  <Download className="mr-2 h-4 w-4 text-slate-500" />
                  <span>Import All Reviews</span>
                </DropdownMenu.Item>
              )}

              {onEditReviewPrefs && (
                <DropdownMenu.Item onClick={onEditReviewPrefs}>
                  <Settings className="mr-2 h-4 w-4 text-slate-500" />
                  <span>Review Settings</span>
                </DropdownMenu.Item>
              )}

              <DropdownMenu.Item
                variant="destructive"
                onClickCapture={handleDisconnectClick}
                className={isConfirmingDisconnect ? "font-bold bg-red-50" : ""}
              >
                <Link2Off className="mr-2 h-4 w-4" />
                <span>{isConfirmingDisconnect ? "Click to Confirm" : "Disconnect"}</span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator />

              <DropdownMenu.Item onClick={handleNeedHelp}>
                <LifeBuoy className="mr-2 h-4 w-4" />
                <span>Need Help?</span>
              </DropdownMenu.Item>
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        </DropdownMenu>
      ) : isLoading ? undefined : (
        <Button
          variant="outline"
          size="icon"
          className="rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors p-1.5 outline-none border-none"
          onClick={handleNeedHelp}
        >
          <span className="sr-only">Need Help?</span>
          <HelpCircle size={25} />
        </Button>
      )}
    </div>
  );
}
